/**
 * Current Events cards (Round 23, Sep 16 2026) — what a Table Talk post
 * renders below its caption:
 *   • NewsCard      post.post_type === 'news'          → headline card, link out
 *   • ScoreCard     post.post_type === 'score'         → one final
 *   • RoundupCard   post.post_type === 'score_roundup' → last night's finals
 * Headline / summary / URL are shown exactly as the feed provided them;
 * the card links to the outlet's article in a new tab. Nothing else on a
 * post changes — likes, comments, shares, reposts all work as usual.
 */
import React, { useState } from 'react';

const border = '1px solid var(--card-border)';
const dim = { color: 'var(--gems-text-dim)' };

const Pill = ({ children, color = 'var(--gems-emerald)' }) => (
  <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full" style={{ color, border: `1px solid ${color}55` }}>{children}</span>
);

// Round 55: a picture narrower than this is a thumbnail — shown small beside the
// headline instead of stretched (and pixelated) across the card.
const SMALL_PICTURE_PX = 320;

export const NewsCard = ({ news }) => {
  const [small, setSmall] = useState(false);
  const [broken, setBroken] = useState(false);
  if (!news) return null;
  const when = news.published_at ? new Date(news.published_at) : null;
  const picture = news.image_url && !broken ? news.image_url : null;
  const onLoad = (e) => { if ((e.target?.naturalWidth || 0) < SMALL_PICTURE_PX) setSmall(true); };
  return (
    <a
      href={news.article_url}
      target="_blank"
      rel="noopener noreferrer"
      className="block mb-3 rounded-xl overflow-hidden hover:opacity-95 transition-opacity"
      style={{ border, background: 'rgba(4,10,8,0.45)' }}
      data-testid="news-card"
    >
      {picture && !small && (
        <img src={picture} alt="" className="w-full max-h-64 object-cover" loading="lazy" onLoad={onLoad} onError={() => setBroken(true)} />
      )}
      <div className="p-3">
        <div className="flex items-center gap-2 mb-1">
          <Pill>Current Events</Pill>
          {when && !isNaN(when.getTime()) && (
            <span className="text-[11px]" style={dim}>{when.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
          )}
        </div>
        {picture && small && (
          <img src={picture} alt="" className="float-right w-16 h-16 ml-3 mb-1 rounded-lg object-cover" loading="lazy" onError={() => setBroken(true)} />
        )}
        <div className="font-bold text-[15px] leading-snug" style={{ color: 'var(--gems-text)' }}>{news.title}</div>
        {news.summary && <p className="text-sm mt-1" style={dim}>{news.summary}</p>}
        <div className="flex items-center justify-between mt-2 text-xs">
          <span style={dim}>via {news.outlet_name}</span>
          <span className="font-semibold" style={{ color: 'var(--gems-emerald)' }}>Read more ↗</span>
        </div>
      </div>
    </a>
  );
};

const TeamRow = ({ team, won }) => (
  <div className="flex items-center gap-3 py-1">
    {team?.logo ? <img src={team.logo} alt="" className="w-7 h-7 object-contain" loading="lazy" /> : <div className="w-7 h-7 rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }} />}
    <div className="flex-1 min-w-0">
      <span className={`text-sm ${won ? 'font-extrabold' : 'font-medium'}`} style={{ color: won ? 'var(--gems-text)' : 'var(--gems-text-dim)' }}>{team?.abbr}</span>
      {team?.name && <span className="text-xs ml-2 hidden sm:inline" style={dim}>{team.name}</span>}
    </div>
    <span className={`text-lg tabular-nums ${won ? 'font-extrabold' : 'font-semibold'}`} style={{ color: won ? 'var(--gems-text)' : 'var(--gems-text-dim)' }}>{team?.score}</span>
  </div>
);

export const ScoreCard = ({ score }) => {
  if (!score || !score.home || !score.away) return null;
  const homeWon = Number(score.home.score) > Number(score.away.score);
  const awayWon = Number(score.away.score) > Number(score.home.score);
  return (
    <div className="mb-3 rounded-xl p-3" style={{ border, background: 'rgba(4,10,8,0.45)' }} data-testid="score-card">
      <div className="flex items-center justify-between mb-1">
        <Pill>{score.league_label || score.league}</Pill>
        <span className="text-[11px] font-extrabold tracking-wide px-2 py-0.5 rounded" style={{ background: 'rgba(52,227,164,0.15)', color: 'var(--gems-emerald)' }}>{score.final_label || 'FINAL'}</span>
      </div>
      <TeamRow team={score.away} won={awayWon} />
      <TeamRow team={score.home} won={homeWon} />
      {/* Round 34: the game's stats page on GEMS (Round 30) — never an outside site */}
      {score.league && score.event_id && (
        <a href={`/api/scores/game/${encodeURIComponent(score.league)}/${encodeURIComponent(score.event_id)}/view`} target="_blank" rel="noopener noreferrer" className="inline-block mt-1 text-xs font-semibold" style={{ color: 'var(--gems-emerald)' }}>Game stats ›</a>
      )}
    </div>
  );
};

export const RoundupCard = ({ score }) => {
  const [open, setOpen] = useState(false);
  if (!score || !Array.isArray(score.games)) return null;
  const rows = open ? score.games : score.games.slice(0, 8);
  return (
    <div className="mb-3 rounded-xl p-3" style={{ border, background: 'rgba(4,10,8,0.45)' }} data-testid="score-roundup-card">
      <div className="flex items-center justify-between mb-2">
        <Pill>{score.league_label || score.league}</Pill>
        <span className="text-[11px]" style={dim}>{score.date} · {score.games.length} final{score.games.length === 1 ? '' : 's'}</span>
      </div>
      <div className="divide-y" style={{ borderColor: 'var(--card-border)' }}>
        {rows.map((g, i) => (
          <div key={i} className="flex items-center justify-between py-1 text-sm tabular-nums">
            <span className={g.winner === 'away' ? 'font-extrabold' : ''} style={{ color: g.winner === 'away' ? 'var(--gems-text)' : 'var(--gems-text-dim)' }}>{g.away} {g.away_score}</span>
            <span className="text-xs px-2" style={dim}>–{g.ot ? ' OT' : ''}</span>
            <span className={g.winner === 'home' ? 'font-extrabold' : ''} style={{ color: g.winner === 'home' ? 'var(--gems-text)' : 'var(--gems-text-dim)' }}>{g.home_score} {g.home}</span>
          </div>
        ))}
      </div>
      {score.games.length > 8 && (
        <button type="button" onClick={() => setOpen(o => !o)} className="mt-2 text-xs font-semibold" style={{ color: 'var(--gems-emerald)' }}>
          {open ? 'Show less' : `Show all ${score.games.length}`}
        </button>
      )}
    </div>
  );
};

/** Drop-in for any post renderer: returns the right card or null. */
// Round 34: a link a member shared in a post — read once by the server when
// the post was created (image, title, description, site).
export const LinkPreviewCard = ({ preview }) => {
  if (!preview?.url) return null;
  return (
    <a
      href={preview.url}
      target="_blank"
      rel="noopener noreferrer nofollow ugc"
      className="block mb-3 rounded-xl overflow-hidden hover:opacity-95 transition-opacity"
      style={{ border, background: 'rgba(4,10,8,0.45)' }}
      data-testid="link-preview-card"
    >
      {preview.image && <img src={preview.image} alt="" className="w-full max-h-64 object-cover" loading="lazy" referrerPolicy="no-referrer" />}
      <div className="p-3">
        <div className="font-bold text-[15px] leading-snug" style={{ color: 'var(--gems-text)' }}>{preview.title}</div>
        {preview.description && <p className="text-sm mt-1 line-clamp-3" style={dim}>{preview.description}</p>}
        <div className="flex items-center justify-between mt-2 text-xs">
          <span style={dim}>{preview.site_name || preview.domain}</span>
          <span className="font-semibold" style={{ color: 'var(--gems-emerald)' }}>Open ↗</span>
        </div>
      </div>
    </a>
  );
};

export const CurrentEventsPostBody = ({ post }) => {
  if (!post) return null;
  if (post.post_type === 'news') return <NewsCard news={post.news} />;
  if (post.post_type === 'score') return <ScoreCard score={post.score} />;
  if (post.post_type === 'score_roundup') return <RoundupCard score={post.score} />;
  if (post.link_preview?.url && !post.media_url) return <LinkPreviewCard preview={post.link_preview} />;
  return null;
};

export default CurrentEventsPostBody;
